const CUSTOMER_API_ME_PATH = '/api/customer/v1/me';
const CUSTOMER_API_TIMEOUT_MS = 12000;

export class CustomerApiClientError extends Error {
  constructor(message, { code = 'CUSTOMER_API_ERROR', status = 0, requestId = '' } = {}) {
    super(message);
    this.name = 'CustomerApiClientError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

export async function loadCustomerIdentity(user) {
  if (!user || typeof user.getIdToken !== 'function') {
    throw new CustomerApiClientError('Sign in to view your customer identity.', { code: 'UNAUTHENTICATED' });
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CUSTOMER_API_TIMEOUT_MS);
  try {
    const idToken = await user.getIdToken(false);
    const response = await fetch(CUSTOMER_API_ME_PATH, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new CustomerApiClientError(
        payload?.safeMessage || 'Customer identity access is temporarily unavailable.',
        {
          code: payload?.code || 'CUSTOMER_API_ERROR',
          status: response.status,
          requestId: payload?.requestId || response.headers.get('x-request-id') || '',
        }
      );
    }
    if (payload?.schemaVersion !== 1 || !payload.customer?.uid || payload.customer.emailVerified !== true) {
      throw new CustomerApiClientError('The customer identity response was invalid.', {
        code: 'INVALID_RESPONSE',
        status: response.status,
      });
    }
    return payload;
  } catch (error) {
    if (error instanceof CustomerApiClientError) throw error;
    if (error?.name === 'AbortError') {
      throw new CustomerApiClientError('Customer identity access timed out.', { code: 'TIMEOUT' });
    }
    throw new CustomerApiClientError('Customer identity access is temporarily unavailable.');
  } finally {
    clearTimeout(timeout);
  }
}

export { CUSTOMER_API_ME_PATH };
